import { useEffect, useRef, useState } from "react";
import Cookies from "js-cookie";
import { getCurrentUser } from "../../pages/chat/api";
import useNotificationSocket from "../../hooks/useNotificationSocket";
import useNotificationsStore from "../../store/useNotificationsStore";

// Invisible app-shell component (mounted once in App.js). It owns the single
// notifications websocket and keeps the store tied to the CURRENT identity.
//
// Why identity matters: this is a single-page app, so logging out/in as a
// different user or switching workspaces does NOT reload the page. Without this,
// the module-level store + the open socket keep the previous account's/
// workspace's data until a manual refresh (the reported bug). We derive an
// identity key (user + workspace) and, whenever it changes, reset and reload.
const computeIdentity = () => {
  let userId = "";
  try {
    userId = getCurrentUser()?.user_id ?? "";
  } catch {
    userId = "";
  }
  const org = Cookies.get("selected_org_id") || "";
  return userId ? `${userId}:${org}` : "";
};

export default function NotificationHost() {
  const [identity, setIdentity] = useState(computeIdentity);
  const prevUserRef = useRef(identity ? String(identity).split(":")[0] : "");

  // Detect identity changes. We react to explicit events for immediacy and poll
  // as a catch-all for same-tab login/logout (which fire no window event, since
  // the app navigates with react-router instead of reloading).
  useEffect(() => {
    const check = () => {
      const next = computeIdentity();
      setIdentity((prev) => (prev === next ? prev : next));
    };
    window.addEventListener("workspace-changed", check);
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", check);
    window.addEventListener("storage", check);
    const interval = setInterval(check, 1000);
    return () => {
      window.removeEventListener("workspace-changed", check);
      window.removeEventListener("focus", check);
      document.removeEventListener("visibilitychange", check);
      window.removeEventListener("storage", check);
      clearInterval(interval);
    };
  }, []);

  // On identity change: clear stale data and load the right data.
  useEffect(() => {
    const store = useNotificationsStore.getState();
    const userId = identity ? String(identity).split(":")[0] : "";
    const userChanged = userId !== prevUserRef.current;
    prevUserRef.current = userId;

    // Different account (or logout) → wipe immediately so no other user's
    // notifications can flash. (Workspace switch keeps the same user, so we skip
    // the wipe and just refresh, avoiding an empty flash.)
    if (userChanged) {
      store.reset();
    }
    if (identity) {
      store.refreshUnreadCount();
      store.loadAll(!userChanged); // silent refresh on workspace switch
    }
  }, [identity]);

  // The socket reconnects (or disconnects) whenever identity changes.
  useNotificationSocket(identity);

  return null;
}
